import { createRef, useState } from "react"
import { RichText } from "prismic-reactjs"
import VisibilitySensor from "react-visibility-sensor"
import SVG from "react-inlinesvg"
import PageSubTitle from "../components/PageSubTitle"
import SEO from "../components/seo"
import Button from "../components/buttons/Button"
import useTranslations from "../hooks/useTranslations"

import * as styles from "./someGuideTemplate.module.css"

const isString = (input) => typeof input === "string"

const CTA = ({ content, dark }) => {
  const { buttonLogin, buttonMore, appLink } = useTranslations()
  return (
    <div className={`${styles.CTA} ${dark ? styles.dark : ""}`}>
      <div className={styles.CTAText}>
        {isString(content) ? (
          <p className="text-zinc-200">{content}</p>
        ) : (
          <RichText render={content} />
        )}
      </div>
      <div className="flex flex-col justify-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
        <Button bgStyle="primary" label={buttonMore} link="/" />
        <Button bgStyle="light" label={buttonLogin} link={appLink} outbound />
      </div>
    </div>
  )
}

const SomeGuideSection = ({
  content,
  cta_content,
  include_cta,
  title,
  section_icon,
  teaser,
}) => {
  return (
    <div className={styles.guideSection}>
      <div className={styles.expandedIconWrapper} />
      <div
        className={`${styles.contentWrapper} ${teaser ? styles.fadeOut : null}`}
      >
        <div className={styles.IconWrapper}>
          <SVG src={section_icon.url} />
        </div>
        <div className={styles.expandedGuideSectionTitle}>{title.text}</div>
        <RichText render={content.richText} />
        {include_cta && <CTA content={cta_content.richText} />}
      </div>
    </div>
  )
}

const Disclaimer = ({ content }) => <small>{content.text}</small>

const Subtitle = ({ subtitle, className }) => (
  <PageSubTitle className={className}>{subtitle.text}</PageSubTitle>
)

const EndScreen = ({ content }) => (
  <div className="mx-4">
    <CTA dark content={content.text} kek="lol" />
  </div>
)

const ScrollDownArrow = ({ className }) => (
  <svg className={className}>
    <path className="a1" d="M0 0 L30 32 L60 0" />
    <path className="a2" d="M0 20 L30 52 L60 20" />
    <path className="a3" d="M0 40 L30 72 L60 40" />
  </svg>
)

const SomeGuideTemplate = ({ pageContext: { content } }) => {
  const refs = content.body.reduce((acc, section) => {
    acc[section.id] = createRef()
    return acc
  }, {})
  const goToSection = (id) => {
    refs[id].current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  const [activeSection, setActiveSection] = useState()
  const sections = content.body.map((section) => {
    const { slice_type, primary, id } = section
    switch (slice_type) {
      case "some_guide_section":
        return (
          <div className={styles.Section}>
            <VisibilitySensor
              onChange={(isVisible) => isVisible && setActiveSection(id)}
            >
              <div className={styles.ScrollDetector} ref={refs[id]} />
            </VisibilitySensor>

            <SomeGuideSection {...primary} teaser={content.teaser} />
            <VisibilitySensor
              onChange={(isVisible) => isVisible && setActiveSection(id)}
            >
              <div className={`end-of-${id} ${styles.ScrollDetector}`} />
            </VisibilitySensor>
          </div>
        )
      case "disclaimer":
        return (
          <div>
            <Disclaimer {...primary} />
            <div className={`end-of-${id}`} />
          </div>
        )
      case "subtitle":
        return (
          <div>
            <VisibilitySensor
              onChange={(isVisible) => isVisible && setActiveSection(id)}
            >
              <div className={styles.ScrollDetector} ref={refs[id]} />
            </VisibilitySensor>
            <Subtitle {...primary} className={styles.Subtitle} />
            <VisibilitySensor
              onChange={(isVisible) => isVisible && setActiveSection(id)}
            >
              <div className={`end-of-${id} ${styles.ScrollDetector}`} />
            </VisibilitySensor>
          </div>
        )
      case "end_screen":
        return <></>

      default:
        return <div>Section {slice_type} not supported</div>
    }
  })
  const navigationItems = () =>
    content.body
      .filter((_) => ["some_guide_section", "subtitle"].includes(_.slice_type))
      .map((_) =>
        _.slice_type === "some_guide_section" ? (
          <h4
            role="link"
            onClick={() => goToSection(_.id)}
            className={activeSection === _.id ? styles.active : ""}
            style={{
              "--icon-url": `url(${_.primary.section_icon.url})`,
            }}
          >
            {_.primary.title.text || " "}
          </h4>
        ) : null,
      )

  const endScreen = () => {
    const data = content.body.find((_) => _.slice_type === "end_screen")
    if (!data) return <></>
    return <EndScreen {...data.primary} />
  }

  return (
    <>
      <SEO title="Someturva" />
      <div className="w-full">
        <div className={styles.header}>
          <img src={content.logo.url} alt="" className="mb-6 w-32 md:mb-10" />
          <h1 className="m-0 mb-4 text-center text-2xl font-extrabold leading-7 tracking-tight text-zinc-900 sm:text-4xl sm:leading-10 md:text-3xl md:leading-9">
            {content.main_title.text}
          </h1>
          <p className="m-2 max-w-xl pb-4 text-center text-base font-semibold leading-snug md:text-xl md:leading-7">
            {content.guide_lead.text}
          </p>
          <ScrollDownArrow className={styles.arrows} />
        </div>
        <div className="flex flex-col justify-center md:flex-row">
          <div className="StickyMenu-trigger" />
          <div className={styles.StickyMenu}>{navigationItems()}</div>
          <div className="mx-auto max-w-prose px-4 sm:px-6 md:mx-0 md:mr-24 md:w-full lg:px-8">
            {sections}
          </div>
        </div>
        {endScreen()}
      </div>
    </>
  )
}

export default SomeGuideTemplate
